import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { intersection, pickBy } from 'lodash';
import PropTypes from 'prop-types';

import {
  fileTypeToExtension,
  getResourceContentTypeAccept,
  getResourceFileMaxSize,
  RESOURCE_CONTENT_TYPE,
  RESOURCE_FILE_TYPE,
} from 'src/data/performance';

const propTypes = {
  allowedFileTypes: PropTypes.arrayOf(Object.values(RESOURCE_FILE_TYPE)),
};

const defaultProps = {
  allowedFileTypes: [],
};

export const UploadResourceFileHelper = forwardRef(
  ({ allowedFileTypes }, ref) => {
    const promiseResolveRef = useRef(null);
    const promiseRejectRef = useRef(null);

    const handleDrop = useCallback((acceptedFiles, fileRejections) => {
      if (!promiseResolveRef.current) {
        return;
      }

      const hasAcceptedFiles = acceptedFiles?.length > 0;
      const hasFileRejections = fileRejections?.length > 0;

      if (hasAcceptedFiles || hasFileRejections) {
        promiseResolveRef.current({
          acceptedFiles: acceptedFiles || [],
          acceptedFile: acceptedFiles?.[0] || null,
          fileRejections: fileRejections || [],
          fileRejection: fileRejections?.[0] || null,
        });
      } else {
        promiseResolveRef.current(null);
      }
    }, []);

    const handleFileDialogCancel = useCallback(() => {
      if (!promiseResolveRef.current) {
        return;
      }

      promiseResolveRef.current(null);
    }, []);

    const handleError = useCallback((error) => {
      if (!promiseRejectRef.current) {
        return;
      }

      promiseRejectRef.current(error);
    }, []);

    const {
      getRootProps: getDocumentRootProps,
      getInputProps: getDocumentInputProps,
      open: openDocumentDropzone,
    } = useDropzone({
      noKeyboard: true,
      noClick: true,
      accept: prepareAccept({
        resourceType: RESOURCE_CONTENT_TYPE.document,
        allowedFileTypes,
      }),
      onDrop: handleDrop,
      onFileDialogCancel: handleFileDialogCancel,
      onError: handleError,
      maxFiles: 1,
      multiple: false,
      maxSize: getResourceFileMaxSize(RESOURCE_CONTENT_TYPE.document),
    });

    const {
      getRootProps: getImageRootProps,
      getInputProps: getImageInputProps,
      open: openImageDropzone,
    } = useDropzone({
      noKeyboard: true,
      noClick: true,
      accept: prepareAccept({
        resourceType: RESOURCE_CONTENT_TYPE.image,
        allowedFileTypes,
      }),
      onDrop: handleDrop,
      onFileDialogCancel: handleFileDialogCancel,
      onError: handleError,
      maxFiles: 1,
      multiple: false,
      maxSize: getResourceFileMaxSize(RESOURCE_CONTENT_TYPE.image),
    });

    const {
      getRootProps: getAudioRootProps,
      getInputProps: getAudioInputProps,
      open: openAudioDropzone,
    } = useDropzone({
      noKeyboard: true,
      noClick: true,
      accept: prepareAccept({
        resourceType: RESOURCE_CONTENT_TYPE.audio,
        allowedFileTypes,
      }),
      onDrop: handleDrop,
      onFileDialogCancel: handleFileDialogCancel,
      onError: handleError,
      maxFiles: 1,
      multiple: false,
      maxSize: getResourceFileMaxSize(RESOURCE_CONTENT_TYPE.audio),
    });

    const {
      getRootProps: getVideoRootProps,
      getInputProps: getVideoInputProps,
      open: openVideoDropzone,
    } = useDropzone({
      noKeyboard: true,
      noClick: true,
      accept: prepareAccept({
        resourceType: RESOURCE_CONTENT_TYPE.video,
        allowedFileTypes,
      }),
      onDrop: handleDrop,
      onFileDialogCancel: handleFileDialogCancel,
      onError: handleError,
      maxFiles: 1,
      multiple: false,
      maxSize: getResourceFileMaxSize(RESOURCE_CONTENT_TYPE.video),
    });

    /**
     * Open file dialog for the given resource type
     *
     * @type {function(RESOURCE_CONTENT_TYPE): Promise<{ acceptedFile: File, acceptedFiles: File[], fileRejection: Object, fileRejections: Object[] } | null>}
     */
    const handleOpenFileDialog = useCallback(
      (resourceType) => {
        const promise = new Promise((resolve, reject) => {
          promiseResolveRef.current = resolve;
          promiseRejectRef.current = reject;
        });

        switch (resourceType) {
          case RESOURCE_CONTENT_TYPE.image:
            openImageDropzone();
            break;
          case RESOURCE_CONTENT_TYPE.audio:
            openAudioDropzone();
            break;
          case RESOURCE_CONTENT_TYPE.video:
            openVideoDropzone();
            break;
          case RESOURCE_CONTENT_TYPE.document:
          default:
            openDocumentDropzone();
            break;
        }

        return promise;
      },
      [openImageDropzone, openAudioDropzone, openVideoDropzone, openDocumentDropzone]
    );

    useImperativeHandle(ref, () => ({
      openFileDialog: handleOpenFileDialog,
    }));

    return (
      <>
        <div {...getDocumentRootProps()}>
          <input {...getDocumentInputProps()} />
        </div>

        <div {...getImageRootProps()}>
          <input {...getImageInputProps()} />
        </div>

        <div {...getAudioRootProps()}>
          <input {...getAudioInputProps()} />
        </div>

        <div {...getVideoRootProps()}>
          <input {...getVideoInputProps()} />
        </div>
      </>
    );
  }
);

UploadResourceFileHelper.displayName = 'UploadResourceFileHelper';
UploadResourceFileHelper.propTypes = propTypes;
UploadResourceFileHelper.defaultProps = defaultProps;

const prepareAccept = ({ resourceType, allowedFileTypes }) => {
  const acceptedFileTypes = getResourceContentTypeAccept(resourceType);

  // If no "allowed file extensions" are provided, return all accepted file types
  if (!allowedFileTypes || allowedFileTypes.length === 0) {
    return acceptedFileTypes;
  }

  // Take only "accepted" entries that has allowed file extensions
  const allowedExtensions = allowedFileTypes.map(fileTypeToExtension);
  return pickBy(acceptedFileTypes, (entry) => {
    return intersection(entry, allowedExtensions).length > 0;
  });
};
